import { useAuth } from './useAuth';
import { useFeatureEntitlement } from './useFeatureEntitlement';
import { usePartnerDeskAccess, type PartnerDeskAccess } from './usePartnerDeskAccess';
import {
  canAccessPartnerNetwork,
  canOpenPartnerDesk,
  partnerNetworkEntitlementCompanyId,
} from '../utils/partnerReferrals';

/** What AppLayout's outlet context carries that the gate reads. */
export interface PartnerReferralsGateContext {
  userData?: { role?: string } | null;
  companyData?: { id?: string; companyId?: string; featureFlags?: Record<string, unknown> | null } | null;
}

export interface MyReferralProgramAccess {
  role: string | null;
  /** The add-on grant is still being asked of the entitlement SSOT (the flag did not decide). */
  addOnLoading: boolean;
  /** The referrer side of Partner Referrals (refer, my clients, rewards, radar, compliance). */
  referrerSide: boolean;
  /** A desk role with the flag or add-on: the server is asked for programs. */
  deskEligible: boolean;
  /** partnerDeskGetContext for this caller (idle when not eligible). */
  desk: PartnerDeskAccess;
  /** At least one program this company operates: show "My referral program". */
  hasProgram: boolean;
  hideCommissions: boolean;
}

/**
 * The one gate for Partner Referrals and "My referral program", shared by the
 * side nav, the route guard and the page so they cannot disagree. The
 * loyalty callables re-check all of it server-side.
 */
export function useMyReferralProgramAccess(ctx: PartnerReferralsGateContext | null | undefined): MyReferralProgramAccess {
  const { user, companyId, hideCommissions } = useAuth();
  const role = ctx?.userData?.role ?? null;
  const flags = ctx?.companyData?.featureFlags ?? null;
  const { hasFeature, loading } = useFeatureEntitlement(partnerNetworkEntitlementCompanyId(ctx?.companyData), 'partner_network');
  const deskEligible = canOpenPartnerDesk(role, flags, hasFeature);
  const cacheKey = deskEligible && user?.uid && companyId ? `${user.uid}:${companyId}:${role}` : null;
  const desk = usePartnerDeskAccess(cacheKey);
  return {
    role,
    addOnLoading: loading,
    referrerSide: canAccessPartnerNetwork(role, flags, hasFeature),
    deskEligible,
    desk,
    // 'loading' with programs only happens on a refresh of the same identity
    // (usePartnerDeskAccess keeps the last answer; a new identity reports none),
    // so a refresh does not unmount the desk or bounce a desk-only viewer.
    hasProgram: (desk.status === 'ready' || desk.status === 'loading') && desk.programs.length > 0,
    hideCommissions: !!hideCommissions,
  };
}
